import React from "react";
import { motion } from "framer-motion";
import { classNames } from "../../../../helpers/classNames";
import Title from "../../../elements/Title";
import { toast } from "react-toastify";
import { postFrontDetails } from "../../../../services/postService";
import { useQuery } from "@tanstack/react-query";
import { lngDetect } from "../../../common/lngDetect";

/* -------------------- DATA (JSON) -------------------- */

const FEATURE_DATA = {
  title: "Become A Biggest Fan",
  description: `
    <p>
      Join thousands of fans supporting their favorite stars. It's simple, fun, and rewarding!
    </p>
  `,
  points: ["Win Big Together", "Power in Numbers", "Why Every Spin Matters"],
};

/* -------------------- ANIMATIONS -------------------- */

const container = {
  hidden: { opacity: 0, y: 40 },
  show: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.8,
      ease: "easeOut",
      staggerChildren: 0.15,
    },
  },
};

const itemAnim = {
  hidden: { y: 20 },
  show: { opacity: 1, y: 0, transition: { duration: 0.7 } },
};

const fadeRight = {
  hidden: { opacity: 0, x: 40 },
  show: { opacity: 1, x: 0, transition: { duration: 0.9 } },
};

/* -------------------- COMPONENT -------------------- */

const Features = () => {
  const { data: featureData } = useQuery({
    queryKey: ["front-feature-section"],
    queryFn: async () => {
      const res = await postFrontDetails({
        slug: "features-section",
      });
      if (res?.status !== 200) {
        toast.warning(res?.message);
      }
      return res?.data;
    },
  });

  const newFeatureData = {
    title: lngDetect(featureData?.content)?.title,
    description: lngDetect(featureData?.content)?.subTitle,
    features:
      (featureData?.sections || [])?.map((item, index) => ({
        icon: item?.icon,
        name: item?.heading,
        description: item?.content,
        color: index % 2 === 0 ? "electric-violet" : "radical-red",
      })) || [],
    points:
      (featureData?.features || []).map((item) => ({
        icon: item?.icon,
        name: item?.heading,
      })) || [],
  };

  console.log({ featureData, newFeatureData });

  return (
    <section
      id="features"
      className="relative py-10 xls:py-24 overflow-hidden z-0"
    >
      {/* Background Glow */}
      <motion.div
        animate={{ opacity: [0.1, 0.2, 0.1], scale: [1, 1.2, 1] }}
        transition={{ duration: 5, repeat: Infinity }}
        className="hidden lg:block absolute -top-[10vh] -left-[10vh] h-1/2 aspect-square bg-radical-red/50 rounded-full blur-3xl z-[-1]"
      />

      <div className="w-full sm:max-w-xl md:max-w-2xl lg:max-w-4xl xls:max-w-6xl xl:max-w-7xl 2xl:max-w-8xl 3xl:max-w-9xl mx-auto px-4">
        <div className="grid xls:grid-cols-2 gap-16">
          {/* LEFT CONTENT */}
          <motion.div
            variants={fadeRight}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true }}
            className="xls:order-2"
          >
            <div className="space-y-8 max-w-xl">
              <Title title="Features" />

              <h2 className="text-2xl sm:text-3xl xl:text-4xl font-bold">
                {newFeatureData.title}
              </h2>

              <div
                className="text-base xl:text-lg space-y-3 text-white/70"
                dangerouslySetInnerHTML={{
                  __html: newFeatureData.description,
                }}
              />

              <div className="space-y-3">
                {newFeatureData.points.map((point, index) => (
                  <motion.div
                    key={index}
                    variants={itemAnim}
                    className="flex items-center space-x-4"
                  >
                    <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-white/5">
                      <i className={`fa-light ${point?.icon}`}></i>
                    </div>
                    <span>{point?.name}</span>
                  </motion.div>
                ))}
              </div>
            </div>
          </motion.div>

          {/* RIGHT GRID */}
          <motion.div
            variants={container}
            whileInView="show"
            viewport={{ once: true }}
            className="grid sm:grid-cols-2 lg:grid-cols-4 xls:grid-cols-2 gap-5 xls:order-1"
          >
            {newFeatureData?.features?.map((item, index) => {
              const isViolet = item.color === "electric-violet";

              return (
                <motion.div
                  key={index}
                  variants={itemAnim}
                  className={classNames(
                    "group relative py-8 px-6 rounded-2xl space-y-4 overflow-hidden bg-woodsmoke shadow transition-all",
                    isViolet
                      ? "hover:shadow-[0px_4px_60px_10px_rgba(134,34,143,0.25)]"
                      : "hover:shadow-[0px_4px_60px_10px_rgba(250,204,21,0.25)]"
                  )}
                >
                  {/* ICON */}
                  <div
                    className={classNames(
                      "w-20 h-20 mx-auto rounded-xl flex items-center justify-center text-3xl transition-all",
                      isViolet
                        ? "bg-electric-violet/10 group-hover:bg-electric-violet/25 text-electric-violet"
                        : "bg-yellow-400/10 group-hover:bg-yellow-400/25 text-yellow-400"
                    )}
                  >
                    <i className={`fa-fw ${item.icon}`} />
                  </div>

                  {/* TEXT */}
                  <div className="text-center space-y-2">
                    <h3 className="text-xl font-semibold">{item.name}</h3>
                    <p className="text-sm text-white/90">{item.description}</p>
                  </div>

                  {/* HOVER EFFECT */}
                  <div
                    className={classNames(
                      "absolute z-[-1] w-80 h-[200%] rotate-[20deg] -top-1/2 -left-[125%] group-hover:left-[100%] transition-all duration-1000",
                      isViolet
                        ? "bg-gradient-to-l from-[rgba(108,40,255,0.2)] via-transparent"
                        : "bg-gradient-to-l from-[rgba(250, 204, 21, 0.7)] via-transparent"
                    )}
                  />
                </motion.div>
              );
            })}
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Features;
